import { HttpClient } from '@angular/common/http';
import { Component, ViewChild, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { GridReadyEvent, ColDef, RowModelType, GridApi, GridOptions, IServerSideDatasource, IServerSideGetRowsParams, ValueFormatterParams, ICellRendererParams, SortModelItem } from 'ag-grid-community';
import { catchError, filter, map, of } from 'rxjs';
import { BeaconDevices } from '../models/beacon-devices.model';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BeaconDevicesModalComponent } from './beacon-devices-modal/beacon-devices-modal.component';
import { ResourceLoader } from '@angular/compiler';

@Component({
  selector: 'app-beacon-devices',
  templateUrl: './beacon-devices.component.html',
  styleUrl: './beacon-devices.component.scss'
})
export class BeaconDevicesComponent {
  public params: any;
  private apiUrl = '/api/beacon_devices/beacon_devices.php';
  private builtUrl = '';

  // grid options
  private gridApi!: GridApi<BeaconDevices>;
  public rowData?: BeaconDevices[];
  public themeClass: string = 'ag-theme-ulaap';
  public rowModelType: RowModelType = 'serverSide';
  public cacheBlockSize = 100;
  public cacheOverflowSize = 2;
  public maxConcurrentDatasourceRequests = 2;
  public infiniteInitialRowCount = 1;
  public gridVisible = true;
  public gridOptions: GridOptions = {};

  defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      buttons: ['apply', 'clear', 'reset']
    },
    autoHeight: true,
    resizable: true,
  };
  
  public columnDefs: ColDef[] = [
    { field: 'beacon_id', headerName: 'Beacon ID', cellStyle: {'font-weight': 'bold'}, pinned: 'left'},
    { field: 'beacon_name', headerName: 'Beacon Name'},
    { field: 'nick_name', headerName: 'Nick Name'},
    { field: 'tid_beacon', headerName: 'TID Beacon'},
    { field: 'beacon_type', headerName: 'Beacon Type'},
    { field: 'mac_address', headerName: 'MAC Address'},    
    { field: 'active', 
      headerName: 'Active' , 
      filter: false,
      width: 130 ,       
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === 0 ? '❌' : '✅';
      }          
    },     
    { field: 'alternate_reference', headerName: 'Work Order Number'},
    { field: 'lading_uuid', headerName: 'Work Order UUID'},
    {
      headerName: 'Link To Work Order', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-link'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.linkToLading(params);
          });
        });
        return button;        
      }, editable: false, width: 80, filter: false
    },       
    { field: 'return_courier', headerName: 'Return Courier'},
    { field: 'tracking_number', headerName: 'Tracking Number'},
    {
      headerName: 'Set Courier', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-truck'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.setCourier(params);
          });
        });
        return button;        
      }, editable: false, width: 80, filter: false
    },       
    {
      headerName: 'Details', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        button.innerText = 'Details';

        button.addEventListener('click', () => {
            this.beaconDeviceDetails(params);
        });
        return button;        
      }, editable: false, width: 120, filter: false,
    },         
  ];

  constructor(private http: HttpClient,       
              private dialog: MatDialog,
              private zone: NgZone,
              private router: Router,
              private activatedRoute: ActivatedRoute
            )
  {}

  ngOnInit()
  {
  }

  onGridReady(params: GridReadyEvent<BeaconDevices>){
    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams<any, any> & { sortModel: SortModelItem[], filterModel: any}) => {
        const sortModel = params.request.sortModel;
        let filterModel = params.request.filterModel;
        filterModel = translateFilters(filterModel);

        const builturl = this.buildUrl(params.request.startRow || 0, params.request.endRow || 100, sortModel, filterModel);
        this.gridApi = params.api;

        // note: we use response.data.data because row data is in a key called "data" in the plugin response
        this.http.get<{ data: {data: BeaconDevices[]}, totalRows: number}>(builturl).subscribe(
          response => {
            params.success({rowData: response.data.data, rowCount: response.totalRows});
          },
          error => {
            console.error('error getting Beacon Device data', error);
            params.fail();
          }
        )
      }
    }
    params.api.setGridOption('serverSideDatasource', dataSource);
  }
  
  // Build get url with parameters
  private buildUrl(startRow: number, endRow: number, sortModel: SortModelItem[], filterModel: any): string {
    console.log('buildUrl() reached')
    console.log(JSON.stringify(filterModel))
    // Construct the URL with query parameters for startRow, endRow, filters, and sortModel
    const filters = encodeURIComponent(JSON.stringify(filterModel));
    const sort = encodeURIComponent(JSON.stringify(sortModel));
    return `${this.apiUrl}?startRow=${startRow}&endRow=${endRow}&filters=${filters}&sortModel=${sort}`;
  }

  linkToLading(params: any) {
    const contact_details_url = `/api/beacon_devices/beacon_devices.php`;    
    const dialogConfig = new MatDialogConfig();
    const beacon_device_uuid = params.data.beacon_device_uuid;
    const alternate_reference = params.data.alternate_reference;

    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '255px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Link To Work Order',
        modalMessage: {beacon_device_uuid: beacon_device_uuid,
                       alternate_reference: alternate_reference
                      },
      },
    };
    const DialogRef = this.dialog.open(BeaconDevicesModalComponent, dialogConfig);
    DialogRef.afterClosed().subscribe(result => {
        window.location.reload();
    });    
  }

  setCourier(params: any) {
    const contact_details_url = `/api/beacon_devices/beacon_devices.php`;    
    const dialogConfig = new MatDialogConfig();
    const beacon_device_uuid = params.data.beacon_device_uuid;
    const return_courier = params.data.return_courier;
    const tracking_number = params.data.tracking_number;

    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '345px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Set Return Courier',
        modalMessage: {beacon_device_uuid: beacon_device_uuid,
                       return_courier: return_courier,
                       tracking_number: tracking_number
                      },
      },
    };
    const DialogRef = this.dialog.open(BeaconDevicesModalComponent, dialogConfig);
    DialogRef.afterClosed().subscribe(result => {
        window.location.reload();
    });    
  }

  scanNewBeacons() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.width = '348px';
    dialogConfig.height = '180px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Scan For New Beacons',
        modalMessage: {
        },
      },
    };

    const DialogRef = this.dialog.open(BeaconDevicesModalComponent, dialogConfig);
    DialogRef.afterClosed().subscribe(result => {
      window.location.reload();
    });
  }

  beaconDeviceDetails(params: any) {
    const beacon_device_uuid = params.data.beacon_device_uuid;
    this.router.navigate(['beacon_device_details', beacon_device_uuid],);
  }

}

// Changes column name to be understandable to plugin
// Swaps filter ranges in case of user error
function translateFilters(filterModel: any){
  const translatedFilters: {[key: string]: string} = {}

  for(const alias in filterModel){
    // switches date values if needed
    if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'date'){
      let startDate = new Date(filterModel[alias].dateFrom);
      let endDate = new Date(filterModel[alias].dateTo);
      if(startDate > endDate){
        const dateHolder = filterModel[alias].dateTo;
        filterModel[alias].dateTo = filterModel[alias].dateFrom;
        filterModel[alias].dateFrom = dateHolder;
      }
    }
    // switch number values if needed
    if(filterModel[alias].type === 'inRange' && filterModel[alias].filterType === 'number' && filterModel[alias].filterTo < filterModel[alias].filter){
      const numHolder = filterModel[alias].filterTo;
      filterModel[alias].filterTo = filterModel[alias].filter;
      filterModel[alias].filter = numHolder;
    }
  }
  return filterModel;
}




